import { useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { shallow } from 'zustand/shallow';
import {
  Button,
  CodeCountdownTimer,
  Icon,
  Input,
  ItemTypeSelect,
  Popup,
  SelectListItem,
  SelectRCCurrencyCoins,
} from '@/components/common';
import { enterAmountSchema } from '@/utils/validation';
import { allowOnlyNumber, formatNumberWithCommas, formatNumberWithMaxLength } from '@/utils/functions';
import { BuyCryptoStoreInterface, useBuyCrypto } from '@/store/buyCrypto';
import {
  IConvertedFiatToCryptoData,
  useConvertFiatToCrypto,
  useGetAmountRange,
  useGetBuyPrices,
} from '@/api/queryHooks/useBuyCryptoController';
import { ItemType } from '@/components/common/form/SelectRC/SelectRCOption';
import { CryptoCurrency, FiatCurrency, PaymentType, Purpose } from '@/api/common.types';
import { buildCoinsData } from '@/hooks/useFiatAndCryptoInputsData';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { OverlayProvider } from 'react-aria';
import { useOverlayTriggerState } from 'react-stately';
import { useRole } from '@/hooks/useRole';
import { Roles } from '@/constants/roles';
import { Verification } from '@/views/SignUp/components';
import { VerificationPending } from '@/components/common/popups';
import { useIsKYBPending } from '@/hooks/useIsKYBPending';
import { useIsKYBVerified } from '@/hooks/useIsKYBVerified';
import { TraceBanner } from '@/components/common/TraceBanner';
import { useWebsiteRedirect } from '@/store/websiteRedirect';
import { useGetCryptoNetworkData } from '@/hooks/useGetCryptoNetworkData';
import { Select } from '@/components/common/form/Select';
import { getCoinIcon } from '@/utils/getCoinIcon';
import NetworkSelectItem from '@/components/common/NetworkSelectItem/NetworkSelectItem';
import { useGetCryptoCoins } from '@/api';
import { useDebounce } from 'usehooks-ts';
import styles from './Steps.module.css';

export interface StepProps {
  step: number;
  setStep: (step: number) => void;
}

const coinsDataInternal = buildCoinsData();

function mapPriceToItemType(
  p: CryptoCurrency | undefined,
  price: string | number | undefined,
  networkFee: string | number | undefined,
): ItemType {
  const coinData = coinsDataInternal.filter((item) => item?.value === p)[0];
  return {
    label: coinData?.label,
    value: coinData?.value.toUpperCase(),
    icon: coinData?.icon,
    rate: price?.toString() ?? '0',
    name: coinData?.name,
    networkFee,
  };
}

const INITIAL_TIMER_FOR_PRICE_UPDATE = 120;

export const EnterAmount = ({ step, setStep }: StepProps) => {
  const { isDesktop } = useMediaQuery();
  const buyState = useBuyCrypto((state) => state, shallow);
  const [timeLeft, setTimeLeft] = useState(INITIAL_TIMER_FOR_PRICE_UPDATE);
  const [convertedData, setConvertedData] = useState<IConvertedFiatToCryptoData | null>(null);
  const [isVerificationVisible, setIsVerificationVisible] = useState(false);
  const accountNotVerifiedState = useOverlayTriggerState({});
  const verificationPendingState = useOverlayTriggerState({});
  const role = useRole();
  const isKYBPendingStatus = useIsKYBPending();
  const isKYBVerified = useIsKYBVerified();
  const websiteRedirect = useWebsiteRedirect((state) => state, shallow);
  const { data: coinsDataValue } = useGetCryptoCoins();

  const [cryptoAmount, setCryptoAmount] = useState('');
  const [selectValue, setSelectValue] = useState<ItemTypeSelect | undefined>(undefined);
  const [priceValue, setPriceValue] = useState<ItemType>();
  const [fiatRange, setFiatRange] = useState({ min: 0, max: 0 });

  const coinsData = useMemo(() => {
    return buildCoinsData(coinsDataValue);
  }, [coinsDataValue]);

  const {
    handleSubmit,
    control,
    clearErrors,
    setError,
    formState: { isValid, errors },
    setValue,
    watch,
    trigger,
  } = useForm<Partial<BuyCryptoStoreInterface>>({
    resolver: zodResolver(enterAmountSchema),
    defaultValues: { pay: '', get: undefined, network: undefined },
    mode: 'onTouched',
  });

  const watchedFiat = watch().pay;
  const watchedCrypto = watch().get;
  const watchedNetwork = watch().network;
  const debouncedFiat = useDebounce(watchedFiat, 1000);

  const {
    data: selectedCoinNetworksData,
    refetch: refetchNetwork,
    isFetching: isNetworksLoading,
  } = useGetCryptoNetworkData({
    cryptoCurrency: watchedCrypto || CryptoCurrency.ETH,
    enabled: false,
    onSuccess: () => {
      setValue('network', undefined);
    },
  });

  const selectedNetworkData = useMemo(
    () => selectedCoinNetworksData.filter((item) => item?.cryptoNetwork === watchedNetwork)[0],
    [selectedCoinNetworksData, watchedNetwork],
  );

  const { refetch: refetchConvertFiatToCrypto, isRefetching: isFiatToCryptoLoading } = useConvertFiatToCrypto([
    {
      crypto: watchedCrypto || '',
      amount: watchedFiat || '',
      fiat: FiatCurrency.AUD,
      purpose: Purpose.BUY,
      isPrivateCall: true,
      group: 'BUY',
      network: watchedNetwork,
    },
    {
      enabled: false,
      onSuccess: (data) => {
        setCryptoAmount(data?.baseAmount?.toString());
        clearErrors('get');
        setConvertedData?.(data);
      },
      onError: (err) => setError('get', { message: err?.response?.data.message }),
    },
  ]);

  const onSubmit: SubmitHandler<Partial<BuyCryptoStoreInterface>> = (data) => {
    if (role === Roles.BUSINESS && !isKYBVerified && !isKYBPendingStatus) {
      return accountNotVerifiedState.open();
    }
    if (role === Roles.BUSINESS && !isKYBVerified && isKYBPendingStatus) {
      return verificationPendingState.open();
    }
    setStep(step + 1);
    buyState.setData({
      ...data,
      rate: Number(priceValue?.rate),
      manualEntryWalletRegex: selectedNetworkData?.walletRegex ?? '',
    });
    return null;
  };

  const {
    refetch: refetchPrice,
    isError: isGetPricesError,
    isFetching: isGetPricesLoading,
  } = useGetBuyPrices([
    {
      amount: 1000,
      fiat: FiatCurrency.AUD,
      payoutmethod: PaymentType.NPP_BANK_TRANSFER,
      crypto: watchedCrypto,
      network: watchedNetwork,
      isPrivateCall: true,
    },
    {
      refetchOnMount: false,
      enabled: false,
      onSuccess: (data) => {
        if (data) {
          setPriceValue(
            mapPriceToItemType(
              watchedCrypto,
              formatNumberWithMaxLength(1000 / Number(data.baseAmount)),
              data?.sendingNetworkFee,
            ),
          );
        }
      },
      onError: (data) => {
        if (data) {
          setPriceValue(mapPriceToItemType(watchedCrypto, '0', '0'));
        }
      },
    },
  ]);

  const { refetch: refetchRange } = useGetAmountRange([
    {
      crypto: watchedCrypto || '',
      fiat: FiatCurrency.AUD,
      paymentType: PaymentType.NPP_BANK_TRANSFER,
      isPrivateCall: true,
    },
    {
      enabled: false,
      onSuccess: (data) => {
        setFiatRange({ min: data?.minimum, max: data?.maximum });
        clearErrors('get');
      },
      onError: (err) => setError('get', { message: err?.response?.data.message }),
    },
  ]);

  const isGeneralLoading = useMemo(
    () => isNetworksLoading || isFiatToCryptoLoading || isGetPricesLoading,
    [isNetworksLoading, isFiatToCryptoLoading, isGetPricesLoading],
  );

  const isGeneralDisabled = useMemo(
    () =>
      !isValid ||
      !cryptoAmount ||
      isGetPricesError ||
      isGeneralLoading ||
      !watchedNetwork ||
      !watchedFiat ||
      +watchedFiat < 1,
    [cryptoAmount, isGeneralLoading, isGetPricesError, isValid, watchedFiat, watchedNetwork],
  );

  useEffect(() => {
    if (timeLeft === 0) {
      setTimeLeft(INITIAL_TIMER_FOR_PRICE_UPDATE);
      refetchPrice();
    }

    if (isGetPricesLoading) {
      setTimeLeft(INITIAL_TIMER_FOR_PRICE_UPDATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, isGetPricesLoading]);

  useEffect(() => {
    // Auto fill from website redirect data
    if (websiteRedirect.flow === 'buy') {
      const currencyValue = coinsData.find((el) => el.value === websiteRedirect.getCurrency);

      setValue('pay', websiteRedirect.pay ?? undefined);

      if (currencyValue) {
        setSelectValue(currencyValue);
        setValue('get', currencyValue.value ?? undefined);
      }

      if (cryptoAmount.length > 0) {
        websiteRedirect.reset();
      }

      if (errors.get) {
        websiteRedirect.reset();
      }

      if (!isFiatToCryptoLoading) {
        trigger();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    coinsData,
    cryptoAmount.length,
    errors.get,
    isFiatToCryptoLoading,
    setSelectValue,
    setValue,
    trigger,
    websiteRedirect,
    websiteRedirect.flow,
    websiteRedirect.pay,
  ]);

  useEffect(() => {
    // Refetch network data on token selectValue change and refetch fiat range data
    if (selectValue !== undefined) {
      refetchNetwork();
      refetchRange();
      trigger();
    }
  }, [refetchNetwork, refetchRange, selectValue, trigger]);

  useEffect(() => {
    // Quote as soon as a crypto is picked. When no network is selected the request hits
    // the no-network endpoint and the backend uses CryptoCurrency.getDefaultNetwork; once
    // the user picks a specific network this effect re-runs with that network in the URL.
    if (selectValue !== undefined) {
      refetchPrice();
      trigger();
    }
  }, [refetchPrice, selectValue, watchedNetwork, trigger]);

  useEffect(() => {
    // "You receive" (convertedData) still needs an explicit network; clear it when none.
    if (!watchedNetwork) {
      setConvertedData(null);
    }
  }, [watchedNetwork]);

  useEffect(() => {
    if (!debouncedFiat || !selectValue || !watchedNetwork) return;

    if (+debouncedFiat >= fiatRange.min && +debouncedFiat <= fiatRange.max) {
      refetchConvertFiatToCrypto();
      trigger();
    } else {
      setCryptoAmount('');
      const isGeneralError = fiatRange.max === 0 && fiatRange.min === fiatRange.max;

      setError('get', {
        message: isGeneralError
          ? ''
          : `Enter between $${formatNumberWithCommas(fiatRange.min)} and $${formatNumberWithCommas(fiatRange.max)}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFiat, fiatRange.max, fiatRange.min, refetchConvertFiatToCrypto, setError, trigger]);

  useEffect(() => {
    if (!debouncedFiat) return;
    if (
      watchedNetwork &&
      watchedNetwork?.length > 0 &&
      +debouncedFiat >= fiatRange.min &&
      +debouncedFiat <= fiatRange.max
    ) {
      refetchConvertFiatToCrypto();
      trigger();
    }
  }, [debouncedFiat, fiatRange, fiatRange.max, fiatRange.min, refetchConvertFiatToCrypto, trigger, watchedNetwork]);

  return (
    <form className={clsx(styles.form, styles.amountForm)} onSubmit={handleSubmit(onSubmit)}>
      <OverlayProvider>
        <Popup isOpen={accountNotVerifiedState.isOpen} onClose={accountNotVerifiedState.close} hasCloseIcon>
          {!isVerificationVisible && (
            <div className={styles.verifyContainer}>
              <h3 className={styles.verifyTitle}>Attention!</h3>
              <p className={clsx(styles.verifyDescription)}>You need to verify your account first</p>
              <Button onPress={() => setIsVerificationVisible(true)} className={styles.button}>
                Verify
              </Button>
            </div>
          )}
          {isVerificationVisible && <Verification />}
        </Popup>
        <Popup isOpen={verificationPendingState.isOpen} onClose={verificationPendingState.close} hasCloseIcon>
          <VerificationPending />
        </Popup>
      </OverlayProvider>
      <div className={styles.amountTitleWrapper}>
        <h2 className={styles.formTitle}>Buy Crypto</h2>
      </div>
      <Controller
        name="pay"
        control={control}
        render={({ field: { onChange, value } }) => {
          return (
            <Input
              onChange={(text) => onChange(allowOnlyNumber(text))}
              value={allowOnlyNumber(value || '').replace(/[.,]\d{3,}/g, (match) => match.slice(0, 3))}
              labelClassName={styles.label}
              size={isDesktop ? 'large' : 'medium'}
              placeholder="200"
              textWithIconRight={FiatCurrency.AUD}
              iconRight="AU"
              label="YOU PAY"
              errorMessage={errors.get?.message}
            />
          );
        }}
      />
      <div className={styles.networkInfo}>
        <div
          className={clsx(
            styles.networkInfoWrapper,
            !isGetPricesLoading && !isGetPricesError && priceValue && priceValue?.rate !== '0'
              ? ''
              : styles.networkHidden,
          )}
        >
          <div className={styles.circle}>
            <Icon name="multiply" size={14} />
          </div>
          {priceValue && priceValue?.rate !== '0' && (
            <div className={styles.priceWrapper}>
              <div className={styles.priceName}>
                <div className={styles.priceBlock}>
                  {priceValue?.icon && <div className={styles?.priceIcon}>{priceValue?.icon}</div>}
                  {priceValue?.label}
                </div>
                price:
              </div>
              <span className={styles.priceValue}>{priceValue?.rate} AUD</span>
            </div>
          )}
        </div>
      </div>
      <div className={clsx(styles.inputWrapper)}>
        <Controller
          name="get"
          control={control}
          render={({ field: { ref, ...rest } }) => (
            <SelectRCCurrencyCoins
              {...rest}
              label="YOU RECEIVE"
              value={cryptoAmount}
              placeholder="0.00"
              size={isDesktop ? 'large' : 'medium'}
              selectValue={selectValue}
              hasValueName={false}
              onSelectChange={async (value) => {
                rest.onChange(value?.value);
                setSelectValue(value);
                // Drop the previously-selected network so the next refetchPrice goes
                // through the no-network endpoint instead of pairing a stale network
                // with the new crypto.
                setValue('network', undefined);
                trigger();
              }}
              coinsData={coinsData}
            />
          )}
        />
      </div>

      <div className={styles.networkInfo}>
        <div
          className={clsx(
            styles.networkInfoWrapper,
            !isGetPricesLoading &&
              !isGetPricesError &&
              priceValue &&
              watchedNetwork &&
              watchedNetwork?.length > 0 &&
              convertedData &&
              convertedData?.sendingNetworkFee > 0
              ? ''
              : styles.networkHidden,
          )}
        >
          <div className={styles.circle}>
            <Icon name="multiply" size={14} />
          </div>
          {convertedData && convertedData?.sendingNetworkFee !== 0 && priceValue && (
            <div className={styles.priceWrapper}>
              <div className={styles.priceName}>
                <div className={styles.priceBlock}>
                  {priceValue?.icon && <div className={styles.priceIcon}>{priceValue?.icon}</div>}Inc. Network Fee:
                </div>
              </div>
              <span className={styles.priceValue}>
                {convertedData?.sendingNetworkFee} {watchedCrypto}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className={clsx(styles.inputWrapper)}>
        <Controller
          name="network"
          control={control}
          render={({ field: { ref, value, ...rest } }) => (
            <Select
              defaultSelectedKey={value}
              isDisabled={isNetworksLoading || selectedCoinNetworksData.length === 0 || !watchedCrypto}
              labelClassName={styles.label}
              inputValue={selectedCoinNetworksData.filter((item) => item?.cryptoNetwork === value)[0]?.description}
              label="CHOOSE NETWORK"
              placeholder="Please select a network"
              onSelectionChange={(e) => {
                setValue('network', e as unknown as CryptoCurrency);
              }}
              filterFn={() => true}
              rightIcon={
                <div className={styles.rightIconContainer}>
                  <div className={styles.rightIcon}>{getCoinIcon(String(watchedNetwork))}</div> {watchedNetwork}
                </div>
              }
              isShowMore
            >
              {selectedCoinNetworksData?.map((item) => {
                return (
                  <SelectListItem key={item?.cryptoNetwork} textValue={item?.description}>
                    <NetworkSelectItem
                      item={item}
                      isFeeVisible={item?.networkFee ? Number(item?.networkFee) > 0 : false}
                    />
                  </SelectListItem>
                );
              })}
            </Select>
          )}
        />
        {!!convertedData && (
          <p className={styles.infoMessage}>
            Withdrawal fee: {convertedData?.sendingNetworkFee} {convertedData?.base} is included to this quote.
          </p>
        )}
      </div>

      {!!watchedFiat?.length && watchedCrypto && watchedNetwork && (
        <div className={clsx(styles.countdownWrapper)}>
          <CodeCountdownTimer
            timeLeft={timeLeft}
            setTimeLeft={setTimeLeft}
            timerText="Price updates in"
            className={styles.priceUpdateWrapper}
          />
        </div>
      )}

      <div className={styles.walletWarning}>
        <Icon name="infoCircled" size={16} className={styles.walletWarningIcon} />
        <p>Please ensure your wallet / address is compatible with your chosen network.</p>
      </div>

      <Button
        className={styles.getStartedButton}
        size={isDesktop ? 'large' : 'medium'}
        type="submit"
        isDisabled={isGeneralDisabled}
        loading={isGeneralLoading}
      >
        Get Started
      </Button>
      <TraceBanner />
    </form>
  );
};
